import './donationLinkViewModal.scss';

import React, { FunctionComponent } from 'react';

import { DonationLink } from '../../../types/__generated-graphQL__';
import { Link } from 'react-router-dom';
import Modal from '@justgiving/modal';
import { PAGES } from '../../constants/pages';
import classNames from 'classnames';
import { formatCurrency } from '../../utils/currencyFormatting';
import formatDate from '../../utils/formatDate';
import getTotalRaised from '../../utils/getTotalRaised';
import { useCharityData } from '../../context/charityContext';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  donationLink: DonationLink | null;
};

const DonationLinkViewModal: FunctionComponent<Props> = ({
  isOpen,
  onRequestClose,
  donationLink,
}) => {
  const { t } = useTranslation();
  const { charity } = useCharityData();

  if (!donationLink) return null;

  const {
    name,
    totalRaised,
    parameters: { recurring, suggestedAmounts, exitUrl, reference },
    createDate,
    author,
    isDefault,
  } = donationLink;

  const currencyCode =
    donationLink?.parameters.currencyCode ?? charity?.settlementCurrencyCode;

  const suggestedFrequency = recurring
    ? t('common.monthlyLabel')
    : t('common.oneOffLabel');

  const getSuggestedAmounts = () => {
    if (!suggestedAmounts?.length || !suggestedAmounts[0]?.description) {
      return null;
    }

    const amountList = suggestedAmounts.map(({ amount, description }, i) => {
      return (
        <li
          className="donationLinkView__suggestedAmounts__item"
          key={`suggestedAmounts-${i}`}
        >
          <span className="donationLinkView__suggestedAmounts__label">
            {t('donationLinks.viewModal.descriptionFor', {
              amount: formatCurrency({ currencyCode, amount }),
            })}
          </span>
          <span className="donationLinkView__suggestedAmounts__value">
            {description}
          </span>
        </li>
      );
    });

    return <ul className="donationLinkView__suggestedAmounts">{amountList}</ul>;
  };

  return (
    <Modal
      {...{ isOpen, onRequestClose }}
      className="donationLinkViewModal"
      style={{
        overlay: { zIndex: 100 },
        content: {
          padding: 0,
          margin: 'auto',
          right: 'unset',
        },
      }}
    >
      <div className="donationLinkView">
        <h2
          className={classNames('jg-text--center', {
            'jg-space-mbmd': isDefault,
          })}
          data-qa="donationLinkViewModal-title"
        >
          {name}
        </h2>
        {!isDefault && (
          <Link
            to={`${PAGES.Frequency}?id=${donationLink.donationLinkId}`}
            className="jg-display-b jg-text--center jg-space-mbmd"
          >
            {t('donationLinks.viewModal.editCheckout')}
          </Link>
        )}
        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.totalRaised')}:
          </span>{' '}
          <span className="donationLinkView__value">
            {getTotalRaised(currencyCode, totalRaised)}
          </span>
        </p>
        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.dateCreated')}:
          </span>{' '}
          <span className="donationLinkView__value">
            {formatDate(createDate)}
          </span>
        </p>
        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.suggestedFrequency')}:
          </span>{' '}
          <span className="donationLinkView__value">{suggestedFrequency}</span>
        </p>

        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.suggestedAmounts')}:
          </span>{' '}
          <span className="donationLinkView__value">
            {suggestedAmounts?.map((sa, index) => (
              <span className="donationLinkView__tag" key={sa.amount}>
                {formatCurrency({ currencyCode, amount: sa.amount })}
              </span>
            ))}
            {!suggestedAmounts?.length && <>-</>}
          </span>
        </p>
        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.exitUrl')}:
          </span>{' '}
          <span className="donationLinkView__value">{exitUrl}</span>
        </p>
        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.reference')}:
          </span>{' '}
          <span className="donationLinkView__value">{reference}</span>
        </p>
        <p className="donationLinkView__row">
          <span className="donationLinkView__label">
            {t('donationLinks.viewModal.author')}:
          </span>{' '}
          <span className="donationLinkView__value">{`${
            author?.firstName ?? '-'
          } ${author?.lastName ?? ''}`}</span>
        </p>
        {getSuggestedAmounts()}
      </div>
    </Modal>
  );
};

export default DonationLinkViewModal;
